/* The two persistent transports on the station, and the state machine that
 * guarantees only one of them can play.
 *
 * THE ELEMENT IS CREATED ONCE AND NEVER AGAIN. It is rendered here, as the
 * first child of the provider, and this provider is mounted above the router in
 * App.tsx — so no navigation in the app can reach it and no re-render can
 * replace it. `src` is set imperatively and never passed as a JSX prop, because
 * a prop React thinks it owns is a prop React will happily reset on a re-render,
 * and resetting `src` on a live stream is a reconnection the listener hears.
 *
 * TWO MODES, MUTUALLY EXCLUSIVE. Native live/local audio and the official
 * SoundCloud iframe share one state machine. Starting either transport stops
 * the other first; no episode page mounts a player of its own. The SoundCloud
 * script and iframe are also absent until a listener deliberately presses Play.
 *
 * LIVE ALWAYS JOINS AT THE EDGE. A live stream has no meaningful position: the
 * only thing you can be is "now". Resuming forty minutes behind is a well-known
 * and genuinely confusing failure, so pausing live is really a STOP — the source
 * is dropped so we stop pulling bytes into a buffer nobody will hear — and
 * pressing play again opens a fresh connection at the live edge.
 *
 * AN EPISODE DOES REMEMBER WHERE YOU WERE, keyed by catalogue number, because a
 * two-hour set is not something anyone finishes in one sitting. Every storage
 * read and write is wrapped: `localStorage` THROWS in a private window in some
 * browsers, and a player that white-screens because somebody browses privately
 * is a worse bug than a forgotten position.
 *
 * MEDIA SESSION IS NOT DECORATION. Almost no small station sets it, and it is
 * the difference between a lock screen, a car dashboard and a Bluetooth head
 * unit reading "Sunday Service — Flytree" and reading "Higher Ground Radio" over
 * and over. It is about fifty lines and it is the most visible fifty lines in
 * this file to anyone listening on a phone.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { EpisodeDetail, LiveState, SoundCloudSchedule } from '../types';
import { useAccount } from '../account';
import { api } from '../api';
import {
  loadSoundCloudApi,
  soundCloudWidgetUrl,
} from './soundcloud';
import type { SoundCloudApi, SoundCloudWidget } from './soundcloud';
import { useLive } from './useLive';

/* --------------------------------------------------------------------- types */

/** Live or on demand. There is no third thing, and never both. */
export type PlayerMode = 'live' | 'episode';
export type PlayerSource = 'native' | 'soundcloud';

export interface PlayerValue {
  /** The last good /api/live answer. Null until the first one lands. */
  live: LiveState | null;
  mode: PlayerMode;
  /** Native live/local audio, or the official SoundCloud widget. */
  source: PlayerSource;
  isPlaying: boolean;
  /** Buffering, connecting, or waiting on a stalled stream. */
  isLoading: boolean;
  /** The SoundCloud widget is configured and awaiting an explicit Play press. */
  soundCloudReady: boolean;
  /** The current episode emitted a real native ended or SoundCloud FINISH event. */
  episodeEnded: boolean;
  /** This episode is the current item in the station's shared off-air schedule. */
  isStationSchedule: boolean;
  /** Shared schedule window and upcoming sets, or null for manual playback/live. */
  stationSchedule: SoundCloudSchedule | null;
  /** Something a listener needs told, in words. Null when all is well. */
  error: string | null;
  /** The episode loaded into the element, or null in live mode. */
  current: EpisodeDetail | null;
  /** Seconds: into the episode, or into the live show. */
  position: number;
  /** Seconds: the episode's length, or the booked length of the live show. */
  duration: number;
  /** 0..1. */
  volume: number;
  muted: boolean;
  playLive: () => void;
  playEpisode: (episode: EpisodeDetail) => void;
  toggle: () => void;
  pause: () => void;
  seek: (seconds: number) => void;
  skip: (seconds: number) => void;
  setVolume: (value: number) => void;
  toggleMute: () => void;
  backToLive: () => void;
  close: () => void;
}

const PlayerContext = createContext<PlayerValue | null>(null);

export function usePlayer(): PlayerValue {
  const value = useContext(PlayerContext);
  if (!value) {
    throw new Error(
      'usePlayer() was called outside <PlayerProvider>. The provider wraps the whole app in ' +
        'App.tsx — if this fired, something is being rendered outside it.',
    );
  }
  return value;
}

/* ------------------------------------------------------------------- storage */

const VOLUME_KEY = 'hgr.volume';
const MUTED_KEY = 'hgr.muted';
const POSITION_PREFIX = 'hgr.pos.';

/** Below this we start from the top: nobody needs resuming eight seconds in. */
const RESUME_FLOOR_SECONDS = 20;
/** And this close to the end, the set is finished. Start it again. */
const RESUME_TAIL_SECONDS = 25;
/** How often a playing episode writes its position down. */
const PERSIST_EVERY_SECONDS = 5;
/** A provider that never becomes playable must not leave the UI connecting forever. */
const SOUNDCLOUD_START_TIMEOUT_MS = 15_000;

function readStore(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private windows throw on access, not just on write.
    return null;
  }
}

function writeStore(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Full, disabled or private. A forgotten position is not worth an exception.
  }
}

function dropStore(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // As above.
  }
}

function positionKey(catalogueNo: string): string {
  return `${POSITION_PREFIX}${catalogueNo.toUpperCase()}`;
}

function initialVolume(): number {
  const raw = readStore(VOLUME_KEY);
  const value = raw === null ? Number.NaN : Number(raw);
  return Number.isFinite(value) && value >= 0 && value <= 1 ? value : 1;
}

function initialMuted(): boolean {
  return readStore(MUTED_KEY) === '1';
}

/* -------------------------------------------------------------------- copy */

/* These are read inside an 84px bar, in a cell that is 320px wide in archive
 * mode. Every one of them is short enough to land in two lines of 11px mono
 * there — a truncated apology is worse than no apology, and the full text is
 * never anywhere else. */
const AUTOPLAY_BLOCKED = 'Press play — your browser blocked the audio starting on its own.';
const NETWORK_LOST = 'The stream dropped. Press play to pick it up again.';
const DECODE_FAILED = 'We could not decode the audio. A reload usually clears it.';
const LIVE_MOUNT_DOWN = 'The stream is not answering. That is our end — try again shortly.';
const EPISODE_GONE = 'That recording would not load. Open the episode to try its source.';
const SOUNDCLOUD_FAILED = 'SoundCloud could not play this set. Open the episode to try it there.';
const STALLED = 'The stream stalled. Waiting for it to pick up.';
const NO_STREAM_YET = 'We have not heard back from the station yet.';

/** How long a stall is allowed to go unremarked before we say something. */
const STALL_GRACE_MS = 8_000;

/* --------------------------------------------------------------------- misc */

function clamp(value: number, low: number, high: number): number {
  if (!Number.isFinite(value)) return low;
  return Math.min(high, Math.max(low, value));
}

/**
 * A live stream is joined at the edge, and a browser handed the same URL twice
 * is entitled to reuse what it has cached. One throwaway query parameter makes
 * every connection a new one; Icecast ignores parameters it does not know.
 */
function liveEdgeUrl(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${Date.now()}`;
}

function hostNames(hosts: { name: string }[]): string {
  return hosts.map((host) => host.name).join(' & ');
}

/* ----------------------------------------------------------------- provider */

export function PlayerProvider({ children }: { children: ReactNode }) {
  const { account, recordListening } = useAccount();
  const { live, error: liveError, refresh: refreshLive } = useLive();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const soundCloudFrameRef = useRef<HTMLIFrameElement | null>(null);

  const [mode, setMode] = useState<PlayerMode>('live');
  const [source, setSource] = useState<PlayerSource>('native');
  const [current, setCurrent] = useState<EpisodeDetail | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [soundCloudReady, setSoundCloudReady] = useState(false);
  const [episodeEnded, setEpisodeEnded] = useState(false);
  const [isStationSchedule, setIsStationSchedule] = useState(false);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [mediaPosition, setMediaPosition] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);
  const [volume, setVolumeState] = useState(initialVolume);
  const [muted, setMuted] = useState(initialMuted);
  const [soundCloudFrameUrl, setSoundCloudFrameUrl] = useState<string | null>(null);
  const [soundCloudFrameGeneration, setSoundCloudFrameGeneration] = useState(0);

  /* Handlers attached to the element read the truth from refs: they are bound
     once, for the life of the tab, and a closure over `mode` would be stale
     within seconds of the first navigation. */
  const modeRef = useRef<PlayerMode>('live');
  const sourceRef = useRef<PlayerSource>('native');
  const currentRef = useRef<EpisodeDetail | null>(null);
  const liveRef = useRef<LiveState | null>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const lastPersistRef = useRef(0);
  const stallRef = useRef<number | null>(null);
  const soundCloudApiRef = useRef<SoundCloudApi | null>(null);
  const soundCloudWidgetRef = useRef<SoundCloudWidget | null>(null);
  const soundCloudReadyRef = useRef(false);
  const soundCloudWantsPlayRef = useRef(false);
  const soundCloudLoadingRef = useRef(false);
  const soundCloudGenerationRef = useRef(0);
  const soundCloudStartTimerRef = useRef<number | null>(null);
  const soundCloudAutoPlayOnReadyRef = useRef(false);
  const stationScheduleRef = useRef(false);
  // Unlike `isPlaying`, this survives the brief FINISH event between two
  // scheduled sets. It is cleared only by an explicit pause/transport change.
  const stationScheduleContinueRef = useRef(false);
  const stationScheduleRequestRef = useRef(0);
  const [stationSchedule, setStationSchedule] = useState<SoundCloudSchedule | null>(null);
  const soundCloudSeekGuardRef = useRef<{
    target: number;
    landed: boolean;
    lastAccepted: number;
    until: number;
  } | null>(null);
  const fallbackToNativeRef = useRef<(episode: EpisodeDetail) => boolean>(() => false);
  const ensureSoundCloudFrameRef = useRef<() => void>(() => undefined);
  const mediaPositionRef = useRef(0);
  const mediaDurationRef = useRef(0);
  const volumeRef = useRef(volume);
  const mutedRef = useRef(muted);

  liveRef.current = live;
  volumeRef.current = volume;
  mutedRef.current = muted;

  const updateMediaPosition = useCallback((seconds: number) => {
    const next = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
    mediaPositionRef.current = next;
    setMediaPosition(next);
  }, []);

  const updateMediaDuration = useCallback((seconds: number) => {
    const next = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
    mediaDurationRef.current = next;
    setMediaDuration(next);
  }, []);

  const beginSoundCloudSeek = useCallback(
    (widget: SoundCloudWidget, seconds: number) => {
      soundCloudSeekGuardRef.current = {
        target: seconds,
        landed: false,
        lastAccepted: seconds,
        until: Date.now() + 5_000,
      };
      widget.seekTo(seconds * 1_000);
      updateMediaPosition(seconds);
    },
    [updateMediaPosition],
  );

  const acceptSoundCloudPosition = useCallback(
    (seconds: number): boolean => {
      const guard = soundCloudSeekGuardRef.current;
      if (guard && Date.now() < guard.until) {
        if (!guard.landed) {
          // A SEEK packet from the old position can arrive after seekTo(). Do
          // not let it undo the listener's requested position on screen.
          if (Math.abs(seconds - guard.target) > 2) return false;
          guard.landed = true;
          guard.lastAccepted = seconds;
        } else {
          // Once the target lands, accept ordinary forward progress but reject
          // a late packet that jumps all the way back to the pre-seek clock.
          if (Math.abs(seconds - guard.lastAccepted) > 5) return false;
          guard.lastAccepted = seconds;
        }
      } else if (guard) {
        soundCloudSeekGuardRef.current = null;
      }
      updateMediaPosition(seconds);
      return true;
    },
    [updateMediaPosition],
  );

  /* ------------------------------------------------------- the live clock */

  /* A 1 Hz tick, and a base point taken the moment a new /api/live lands. The
     base is a LOCAL delta — elapsed at receipt, plus wall time since — so a
     visitor whose clock is wrong still sees a counter that runs at one second
     per second. Only live needs this; an episode's position comes off the
     element itself. */
  const [clock, setClock] = useState(() => Date.now());
  const elapsedBaseRef = useRef<{ elapsed: number; at: number } | null>(null);
  const liveSeenRef = useRef<LiveState | null | undefined>(undefined);

  if (liveSeenRef.current !== live) {
    liveSeenRef.current = live;
    elapsedBaseRef.current = live?.now
      ? { elapsed: live.now.elapsed_seconds, at: Date.now() }
      : null;
  }

  const showIsOn = mode === 'live' && Boolean(live?.now);

  useEffect(() => {
    if (!showIsOn) return;
    const id = window.setInterval(() => setClock(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [showIsOn]);

  const liveElapsed = (() => {
    const base = elapsedBaseRef.current;
    if (!base) return 0;
    return Math.max(0, base.elapsed + (clock - base.at) / 1000);
  })();

  const liveShowLength = (() => {
    const now = live?.now;
    if (!now) return 0;
    const length = (Date.parse(now.ends_at) - Date.parse(now.starts_at)) / 1000;
    return Number.isFinite(length) && length > 0 ? length : 0;
  })();

  /* --------------------------------------------------------- stall timing */

  const clearStall = useCallback(() => {
    if (stallRef.current !== null) {
      window.clearTimeout(stallRef.current);
      stallRef.current = null;
    }
  }, []);

  const armStall = useCallback(() => {
    if (stallRef.current !== null) return;
    stallRef.current = window.setTimeout(() => {
      stallRef.current = null;
      setPlaybackError(STALLED);
    }, STALL_GRACE_MS);
  }, []);

  /* -------------------------------------------------------- position store */

  const savePosition = useCallback((force = false) => {
    const episode = currentRef.current;
    if (!episode || modeRef.current !== 'episode') return;
    // Scheduled playback belongs to the station clock, not to the listener's
    // personal resume history. Opening this set manually later must not jump to
    // wherever the shared programme happened to be.
    if (stationScheduleRef.current) return;

    const audio = audioRef.current;
    const at = sourceRef.current === 'soundcloud'
      ? mediaPositionRef.current
      : audio?.currentTime;
    if (typeof at !== 'number' || !Number.isFinite(at)) return;

    const now = Date.now();
    if (!force && now - lastPersistRef.current < PERSIST_EVERY_SECONDS * 1000) return;
    lastPersistRef.current = now;

    const length = sourceRef.current === 'soundcloud'
      ? (mediaDurationRef.current || episode.duration_seconds)
      : audio && Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : episode.duration_seconds;

    // Neither end of a set is worth remembering: the top is where it starts
    // anyway, and the last half-minute means it is finished.
    if (at < RESUME_FLOOR_SECONDS || (length > 0 && at > length - RESUME_TAIL_SECONDS)) {
      dropStore(positionKey(episode.catalogue_no));
      return;
    }
    writeStore(positionKey(episode.catalogue_no), String(Math.floor(at)));
  }, []);

  const resumeFor = useCallback((episode: EpisodeDetail): number | null => {
    const raw = readStore(positionKey(episode.catalogue_no));
    if (raw === null) return null;
    const seconds = Number(raw);
    if (!Number.isFinite(seconds) || seconds < RESUME_FLOOR_SECONDS) return null;
    const length = episode.duration_seconds;
    if (length > 0 && seconds > length - RESUME_TAIL_SECONDS) return null;
    return seconds;
  }, []);

  /* ------------------------------------------------------------- playback */

  /** Drop the source outright: pausing a stream still downloads it. */
  const stopSource = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.removeAttribute('src');
    // Without this the browser keeps the socket open to a mount nobody is
    // listening to. `load()` with no source ends quietly — no error event.
    audio.load();
  }, []);

  const clearSoundCloudStartTimer = useCallback(() => {
    if (soundCloudStartTimerRef.current === null) return;
    window.clearTimeout(soundCloudStartTimerRef.current);
    soundCloudStartTimerRef.current = null;
  }, []);

  const pauseSoundCloud = useCallback(() => {
    clearSoundCloudStartTimer();
    soundCloudWantsPlayRef.current = false;
    soundCloudLoadingRef.current = false;
    soundCloudAutoPlayOnReadyRef.current = false;
    soundCloudSeekGuardRef.current = null;
    const widget = soundCloudWidgetRef.current;
    if (widget) widget.pause();
    else {
      // The API/script may still be loading. Removing the not-yet-controlled
      // iframe prevents a stale READY state appearing after we switched.
      soundCloudGenerationRef.current += 1;
      soundCloudWidgetRef.current = null;
      soundCloudApiRef.current = null;
      soundCloudReadyRef.current = false;
      setSoundCloudFrameUrl(null);
      setSoundCloudReady(false);
    }
  }, [clearSoundCloudStartTimer]);

  const disposeSoundCloud = useCallback(() => {
    clearSoundCloudStartTimer();
    soundCloudGenerationRef.current += 1;
    soundCloudWantsPlayRef.current = false;
    soundCloudLoadingRef.current = false;
    soundCloudAutoPlayOnReadyRef.current = false;
    soundCloudSeekGuardRef.current = null;

    const widget = soundCloudWidgetRef.current;
    const events = soundCloudApiRef.current?.Widget.Events;
    if (widget) {
      widget.pause();
      if (events) Object.values(events).forEach((event) => widget.unbind(event));
    }

    soundCloudWidgetRef.current = null;
    soundCloudApiRef.current = null;
    soundCloudReadyRef.current = false;
    setSoundCloudFrameUrl(null);
    setSoundCloudReady(false);
  }, [clearSoundCloudStartTimer]);

  const armSoundCloudStartTimer = useCallback(
    (episode: EpisodeDetail, generation: number) => {
      clearSoundCloudStartTimer();
      soundCloudStartTimerRef.current = window.setTimeout(() => {
        soundCloudStartTimerRef.current = null;
        if (
          soundCloudGenerationRef.current !== generation ||
          sourceRef.current !== 'soundcloud' ||
          currentRef.current !== episode ||
          !soundCloudLoadingRef.current
        ) return;
        if (fallbackToNativeRef.current(episode)) return;
        disposeSoundCloud();
        setIsPlaying(false);
        setIsLoading(false);
        setPlaybackError(SOUNDCLOUD_FAILED);
      }, SOUNDCLOUD_START_TIMEOUT_MS);
    },
    [clearSoundCloudStartTimer, disposeSoundCloud],
  );

  const prepareSoundCloud = useCallback(() => {
    const widget = soundCloudWidgetRef.current;
    const episode = currentRef.current;
    if (!widget) return;
    if (
      !episode ||
      sourceRef.current !== 'soundcloud'
    ) {
      widget.pause();
      return;
    }

    widget.setVolume(mutedRef.current ? 0 : Math.round(volumeRef.current * 100));
    widget.getDuration((milliseconds) => {
      if (
        soundCloudWidgetRef.current !== widget ||
        sourceRef.current !== 'soundcloud' ||
        currentRef.current !== episode
      ) return;
      if (Number.isFinite(milliseconds) && milliseconds > 0) {
        updateMediaDuration(milliseconds / 1_000);
      }
    });

    const wanted = pendingSeekRef.current;
    if (wanted !== null) {
      beginSoundCloudSeek(widget, wanted);
      pendingSeekRef.current = null;
    }
  }, [beginSoundCloudSeek, updateMediaDuration]);

  /* The iframe and its API are created only after an explicit SoundCloud play
     request. Once created they stay above the router with the native element,
     so navigation cannot interrupt the set. */
  useEffect(() => {
    if (!soundCloudFrameUrl) return;
    const generation = soundCloudFrameGeneration;
    const episode = currentRef.current;
    if (!episode) return;
    let cancelled = false;

    void loadSoundCloudApi()
      .then((api) => {
        if (
          cancelled ||
          soundCloudGenerationRef.current !== generation ||
          sourceRef.current !== 'soundcloud' ||
          currentRef.current !== episode
        ) return;
        const frame = soundCloudFrameRef.current;
        if (!frame) return;

        soundCloudApiRef.current = api;
        const widget = api.Widget(frame);
        soundCloudWidgetRef.current = widget;

        const events = api.Widget.Events;
        const isCurrentLoad = () => (
          soundCloudGenerationRef.current === generation &&
          soundCloudWidgetRef.current === widget &&
          sourceRef.current === 'soundcloud' &&
          currentRef.current === episode
        );

        widget.bind(events.READY, () => {
          if (!isCurrentLoad()) {
            widget.pause();
            return;
          }
          soundCloudReadyRef.current = true;
          prepareSoundCloud();
          const playOnReady = soundCloudAutoPlayOnReadyRef.current;
          soundCloudAutoPlayOnReadyRef.current = false;
          // Remember the click that created the iframe. Loading SoundCloud is
          // asynchronous, but a listener should not have to press Play again
          // after READY merely because the transport did not exist yet.
          clearSoundCloudStartTimer();
          if (playOnReady) {
            soundCloudWantsPlayRef.current = true;
            soundCloudLoadingRef.current = true;
            setSoundCloudReady(true);
            setIsPlaying(false);
            setIsLoading(true);
            armSoundCloudStartTimer(episode, generation);
            widget.play();
            return;
          }
          soundCloudWantsPlayRef.current = false;
          soundCloudLoadingRef.current = false;
          setSoundCloudReady(true);
          setIsPlaying(false);
          setIsLoading(false);
          setPlaybackError(null);
        });
        widget.bind(events.PLAY, () => {
          if (!isCurrentLoad()) {
            // A late provider start must fail closed, but it must not overwrite
            // the shared state of native live/local audio now playing instead.
            widget.pause();
            return;
          }
          if (!soundCloudWantsPlayRef.current) {
            widget.pause();
            setIsPlaying(false);
            setIsLoading(false);
            return;
          }
          clearSoundCloudStartTimer();
          soundCloudLoadingRef.current = false;
          soundCloudReadyRef.current = true;
          if (stationScheduleRef.current) stationScheduleContinueRef.current = true;
          setSoundCloudReady(true);
          setIsPlaying(true);
          setIsLoading(false);
          setPlaybackError(null);
        });
        widget.bind(events.PAUSE, () => {
          if (!isCurrentLoad()) return;
          if (soundCloudLoadingRef.current && soundCloudWantsPlayRef.current) return;
          soundCloudWantsPlayRef.current = false;
          setIsPlaying(false);
          setIsLoading(false);
          savePosition(true);
        });
        widget.bind(events.PLAY_PROGRESS, (progress) => {
          if (!isCurrentLoad() || soundCloudLoadingRef.current) return;
          const milliseconds = progress?.currentPosition;
          if (typeof milliseconds !== 'number' || !Number.isFinite(milliseconds)) return;
          const seconds = milliseconds / 1_000;
          if (!acceptSoundCloudPosition(seconds)) return;
          savePosition();
        });
        widget.bind(events.SEEK, (progress) => {
          if (!isCurrentLoad()) return;
          const milliseconds = progress?.currentPosition;
          if (typeof milliseconds === 'number' && Number.isFinite(milliseconds)) {
            acceptSoundCloudPosition(milliseconds / 1_000);
          }
        });
        widget.bind(events.FINISH, () => {
          if (!isCurrentLoad() || soundCloudLoadingRef.current) return;
          clearSoundCloudStartTimer();
          const seekGuard = soundCloudSeekGuardRef.current;
          if (
            seekGuard &&
            Date.now() < seekGuard.until &&
            seekGuard.target < mediaDurationRef.current - 2
          ) return;
          soundCloudSeekGuardRef.current = null;
          soundCloudWantsPlayRef.current = false;
          soundCloudLoadingRef.current = false;
          soundCloudReadyRef.current = false;
          setSoundCloudReady(false);
          pendingSeekRef.current = null;
          setIsPlaying(false);
          setIsLoading(false);
          setEpisodeEnded(true);
          updateMediaPosition(mediaDurationRef.current || episode.duration_seconds);
          if (!stationScheduleRef.current) dropStore(positionKey(episode.catalogue_no));
        });
        widget.bind(events.ERROR, () => {
          if (!isCurrentLoad()) return;
          clearSoundCloudStartTimer();
          if (fallbackToNativeRef.current(episode)) return;
          soundCloudWantsPlayRef.current = false;
          soundCloudLoadingRef.current = false;
          soundCloudReadyRef.current = false;
          setSoundCloudReady(false);
          setIsPlaying(false);
          setIsLoading(false);
          setPlaybackError(SOUNDCLOUD_FAILED);
        });

      })
      .catch(() => {
        if (cancelled || soundCloudGenerationRef.current !== generation) return;
        clearSoundCloudStartTimer();
        soundCloudWidgetRef.current = null;
        soundCloudApiRef.current = null;
        soundCloudReadyRef.current = false;
        setSoundCloudFrameUrl(null);
        if (sourceRef.current !== 'soundcloud' || currentRef.current !== episode) return;
        if (fallbackToNativeRef.current(episode)) return;
        soundCloudWantsPlayRef.current = false;
        soundCloudLoadingRef.current = false;
        setSoundCloudReady(false);
        setIsPlaying(false);
        setIsLoading(false);
        setPlaybackError(SOUNDCLOUD_FAILED);
      });

    return () => {
      cancelled = true;
    };
  }, [
    acceptSoundCloudPosition,
    armSoundCloudStartTimer,
    clearSoundCloudStartTimer,
    prepareSoundCloud,
    savePosition,
    soundCloudFrameGeneration,
    soundCloudFrameUrl,
    updateMediaPosition,
  ]);

  const attemptPlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // A set played to the end sits at its duration. Pressing play there means
    // "again", not "nothing happens".
    if (
      modeRef.current === 'episode' &&
      Number.isFinite(audio.duration) &&
      audio.duration > 0 &&
      audio.currentTime >= audio.duration - 0.5
    ) {
      audio.currentTime = 0;
      updateMediaPosition(0);
    }

    setEpisodeEnded(false);
    setIsLoading(true);
    const started = audio.play();
    if (!started) return;

    started
      .then(() => {
        setPlaybackError(null);
      })
      .catch((error: unknown) => {
        const name = error instanceof DOMException ? error.name : '';
        // A play() superseded by another load or pause. Ours, not theirs.
        if (name === 'AbortError') return;
        setIsPlaying(false);
        setIsLoading(false);
        setPlaybackError(name === 'NotAllowedError' ? AUTOPLAY_BLOCKED : NETWORK_LOST);
      });
  }, [updateMediaPosition]);

  const attemptSoundCloudPlay = useCallback(() => {
    const episode = currentRef.current;
    if (sourceRef.current !== 'soundcloud' || !episode) return;
    soundCloudWantsPlayRef.current = true;
    soundCloudLoadingRef.current = true;
    setEpisodeEnded(false);
    setIsLoading(true);
    setPlaybackError(null);

    const widget = soundCloudWidgetRef.current;
    if (widget) {
      // If Widget exists but READY has not arrived, repeat this request from
      // the READY handler. A ready widget consumes the click immediately.
      soundCloudAutoPlayOnReadyRef.current = !soundCloudReadyRef.current;
      armSoundCloudStartTimer(episode, soundCloudGenerationRef.current);
      if (
        mediaDurationRef.current > 0 &&
        mediaPositionRef.current >= mediaDurationRef.current - 0.5
      ) {
        pendingSeekRef.current = 0;
        updateMediaPosition(0);
      }
      // READY may have arrived while the listener had this paused. Re-apply
      // saved volume and any pending resume/seek before asking the widget to
      // play. These commands are posted in order, synchronously inside this
      // listener activation, so strict mobile browsers accept the final play.
      prepareSoundCloud();
      widget.play();
      return;
    }

    soundCloudAutoPlayOnReadyRef.current = true;
    ensureSoundCloudFrameRef.current();
  }, [armSoundCloudStartTimer, prepareSoundCloud, updateMediaPosition]);

  const fallbackToNative = useCallback(
    (episode: EpisodeDetail): boolean => {
      const audio = audioRef.current;
      if (
        !audio ||
        !episode.audio_url ||
        sourceRef.current !== 'soundcloud' ||
        currentRef.current !== episode
      ) return false;

      const at = mediaPositionRef.current;
      const shouldPlay = soundCloudWantsPlayRef.current;
      disposeSoundCloud();
      stopSource();

      sourceRef.current = 'native';
      pendingSeekRef.current = at > 0 ? at : null;
      setSource('native');
      setEpisodeEnded(false);
      updateMediaPosition(at);
      updateMediaDuration(episode.duration_seconds > 0 ? episode.duration_seconds : 0);
      setPlaybackError(null);
      setIsPlaying(false);
      setIsLoading(shouldPlay);

      audio.preload = 'auto';
      audio.src = episode.audio_url;
      audio.load();
      if (shouldPlay) attemptPlay();
      return true;
    },
    [attemptPlay, disposeSoundCloud, stopSource, updateMediaDuration, updateMediaPosition],
  );
  fallbackToNativeRef.current = fallbackToNative;

  const ensureSoundCloudFrame = useCallback(() => {
    const episode = currentRef.current;
    const trackUrl = episode?.soundcloud_url;
    if (
      !trackUrl ||
      sourceRef.current !== 'soundcloud' ||
      !soundCloudLoadingRef.current
    ) return;

    // The iframe is intentionally non-autoplaying. It may be created after an
    // async episode fetch, so READY only configures it; a later explicit press
    // starts playback inside a fresh activation task.
    const generation = soundCloudGenerationRef.current + 1;
    soundCloudGenerationRef.current = generation;
    soundCloudReadyRef.current = false;
    setSoundCloudReady(false);
    setSoundCloudFrameGeneration(generation);
    setSoundCloudFrameUrl(soundCloudWidgetUrl(trackUrl));
    void loadSoundCloudApi().catch(() => undefined);
    armSoundCloudStartTimer(episode, generation);
  }, [armSoundCloudStartTimer]);
  ensureSoundCloudFrameRef.current = ensureSoundCloudFrame;

  const playLive = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const state = liveRef.current;
    if (!state) {
      setPlaybackError(NO_STREAM_YET);
      refreshLive();
      return;
    }

    // Whatever was on before gives up its place first. One element, one source.
    savePosition(true);
    clearStall();
    disposeSoundCloud();
    stopSource();
    stationScheduleRequestRef.current += 1;
    stationScheduleContinueRef.current = false;
    currentRef.current = null;
    stationScheduleRef.current = false;
    modeRef.current = 'live';
    sourceRef.current = 'native';
    pendingSeekRef.current = null;
    setCurrent(null);
    setIsStationSchedule(false);
    setStationSchedule(null);
    setMode('live');
    setSource('native');
    setEpisodeEnded(false);
    updateMediaPosition(0);
    updateMediaDuration(0);
    setPlaybackError(null);
    setIsPlaying(false);
    setIsLoading(true);

    audio.preload = 'auto';
    audio.src = liveEdgeUrl(state.stream.url);
    audio.load();
    attemptPlay();
  }, [
    attemptPlay,
    clearStall,
    refreshLive,
    savePosition,
    disposeSoundCloud,
    stopSource,
    updateMediaDuration,
    updateMediaPosition,
  ]);

  const playEpisode = useCallback(
    (episode: EpisodeDetail) => {
      const audio = audioRef.current;
      if (!audio) return;

      // A deliberate archive choice leaves the automatic station rotation.
      stationScheduleRequestRef.current += 1;
      stationScheduleRef.current = false;
      stationScheduleContinueRef.current = false;
      soundCloudAutoPlayOnReadyRef.current = false;
      setIsStationSchedule(false);
      setStationSchedule(null);

      const nextSource: PlayerSource = episode.soundcloud_url ? 'soundcloud' : 'native';

      // A page can remain as a catalogue/tracklist record with no playable
      // source. Refuse it without knocking the live stream off.
      if (!episode.soundcloud_url && !episode.audio_url) {
        setPlaybackError(`${episode.catalogue_no} has no playable copy attached.`);
        return;
      }

      // Already loaded: this is a resume, not a reload. Reloading would throw
      // away the position and re-download the file.
      if (
        modeRef.current === 'episode' &&
        currentRef.current?.catalogue_no === episode.catalogue_no &&
        sourceRef.current === nextSource &&
        (nextSource === 'soundcloud' || audio.currentSrc)
      ) {
        if (nextSource === 'soundcloud') attemptSoundCloudPlay();
        else if (audio.paused) attemptPlay();
        return;
      }

      savePosition(true);
      clearStall();
      disposeSoundCloud();
      stopSource();
      currentRef.current = episode;
      modeRef.current = 'episode';
      sourceRef.current = nextSource;
      lastPersistRef.current = 0;
      pendingSeekRef.current = resumeFor(episode);
      setCurrent(episode);
      setMode('episode');
      setSource(nextSource);
      setEpisodeEnded(false);
      updateMediaPosition(pendingSeekRef.current ?? 0);
      updateMediaDuration(episode.duration_seconds > 0 ? episode.duration_seconds : 0);
      setPlaybackError(null);
      setIsPlaying(false);
      setIsLoading(true);

      if (nextSource === 'soundcloud' && episode.soundcloud_url) {
        // This was an explicit Play press. Carry that intent through the
        // asynchronous iframe/API setup so READY starts the track once.
        soundCloudAutoPlayOnReadyRef.current = true;
        soundCloudWantsPlayRef.current = true;
        soundCloudLoadingRef.current = true;
        ensureSoundCloudFrameRef.current();
        return;
      }

      audio.preload = 'auto';
      audio.src = episode.audio_url ?? '';
      audio.load();
      attemptPlay();
    },
    [
      attemptPlay,
      attemptSoundCloudPlay,
      clearStall,
      disposeSoundCloud,
      resumeFor,
      savePosition,
      stopSource,
      updateMediaDuration,
      updateMediaPosition,
    ],
  );

  const selectStationSchedule = useCallback(
    (schedule: SoundCloudSchedule, continuePlaying: boolean) => {
      const episode = schedule.current;
      const audio = audioRef.current;
      if (!audio || !episode.soundcloud_url) return;

      savePosition(true);
      clearStall();
      disposeSoundCloud();
      stopSource();

      currentRef.current = episode;
      modeRef.current = 'episode';
      sourceRef.current = 'soundcloud';
      stationScheduleRef.current = true;
      stationScheduleContinueRef.current = continuePlaying;
      lastPersistRef.current = 0;
      // Join the common programme at its current position. Personal archive
      // listens still keep their own saved position through playEpisode().
      pendingSeekRef.current = schedule.elapsed_seconds;
      setCurrent(episode);
      setStationSchedule(schedule);
      setMode('episode');
      setSource('soundcloud');
      setIsStationSchedule(true);
      setEpisodeEnded(false);
      updateMediaPosition(schedule.elapsed_seconds);
      updateMediaDuration(episode.duration_seconds > 0 ? episode.duration_seconds : 0);
      setPlaybackError(null);
      setIsPlaying(false);
      setIsLoading(continuePlaying);

      soundCloudAutoPlayOnReadyRef.current = continuePlaying;
      soundCloudWantsPlayRef.current = continuePlaying;
      soundCloudLoadingRef.current = continuePlaying;
      if (continuePlaying) ensureSoundCloudFrameRef.current();
    },
    [
      clearStall,
      disposeSoundCloud,
      savePosition,
      stopSource,
      updateMediaDuration,
      updateMediaPosition,
    ],
  );

  const requestStationSchedule = useCallback(
    async (continuePlaying: boolean) => {
      const request = stationScheduleRequestRef.current + 1;
      stationScheduleRequestRef.current = request;
      try {
        const schedule = await api.getSoundCloudSchedule();
        if (stationScheduleRequestRef.current !== request) return;
        if (modeRef.current !== 'live' && !stationScheduleRef.current) return;

        // A listener who pressed Play before this small metadata request
        // returned is already hearing the native 24/7 stream. Do not cut it off
        // and replace it with a configured-but-paused third-party player.
        const audio = audioRef.current;
        if (
          !continuePlaying &&
          modeRef.current === 'live' &&
          audio &&
          !audio.paused
        ) return;

        selectStationSchedule(schedule, continuePlaying);
      } catch {
        if (stationScheduleRequestRef.current !== request) return;
        if (continuePlaying && stationScheduleRef.current) {
          setIsPlaying(false);
          setIsLoading(false);
          setPlaybackError('The SoundCloud schedule could not be refreshed. Press play to retry.');
        }
        // With no eligible SoundCloud set, live mode remains the native 24/7
        // stream. That fallback is intentionally silent here, not an app error.
      }
    },
    [selectStationSchedule],
  );

  /* While no show is live, stage the public SoundCloud set assigned to the
     current shared schedule window. This fetches HGR metadata only;
     SoundCloud's script and iframe remain absent until the listener presses
     Play. */
  useEffect(() => {
    if (!live || live.live) return;
    if (modeRef.current !== 'live' || currentRef.current !== null) return;
    void requestStationSchedule(false);
  }, [current, live?.live, mode, requestStationSchedule]);

  /* Refresh at the shared programme boundary. Everyone asks the server for the
     same next set; an active listener continues while a paused one stays
     paused. A manually selected archive episode is not part of this loop. */
  useEffect(() => {
    if (!isStationSchedule || !stationSchedule) return;
    const delay = Math.max(1_000, (stationSchedule.remaining_seconds + 1) * 1_000);
    const timer = window.setTimeout(() => {
      void requestStationSchedule(stationScheduleContinueRef.current);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isStationSchedule, requestStationSchedule, stationSchedule]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    clearStall();
    if (modeRef.current === 'live') {
      // Pausing live is a stop. There is nothing to come back to.
      stopSource();
      return;
    }
    if (sourceRef.current === 'soundcloud') {
      if (stationScheduleRef.current) stationScheduleContinueRef.current = false;
      pauseSoundCloud();
      setIsPlaying(false);
      setIsLoading(false);
      savePosition(true);
      return;
    }
    audio.pause();
    savePosition(true);
  }, [clearStall, pauseSoundCloud, savePosition, stopSource]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (modeRef.current === 'episode') {
      if (!currentRef.current) {
        playLive();
        return;
      }
      if (sourceRef.current === 'soundcloud') {
        if (soundCloudLoadingRef.current || soundCloudWantsPlayRef.current) pause();
        else attemptSoundCloudPlay();
        return;
      }
      if (audio.paused) attemptPlay();
      else pause();
      return;
    }

    // Live: no source, or a stopped one, means open a new connection at the
    // edge rather than resume a buffer that is now minutes old.
    if (audio.paused || !audio.currentSrc) playLive();
    else pause();
  }, [attemptPlay, attemptSoundCloudPlay, pause, playLive]);

  const seek = useCallback(
    (seconds: number) => {
      const audio = audioRef.current;
      const episode = currentRef.current;
      if (!audio || !episode || modeRef.current !== 'episode') return;
      // A shared programme is joined at the station's current position; a
      // listener seeking it would no longer be hearing the scheduled set.
      if (stationScheduleRef.current) return;

      const length = sourceRef.current === 'soundcloud'
        ? (mediaDurationRef.current || episode.duration_seconds)
        : Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : episode.duration_seconds;
      const target = clamp(seconds, 0, length > 0 ? length : Math.max(0, seconds));
      setEpisodeEnded(false);
      if (sourceRef.current === 'soundcloud') {
        pendingSeekRef.current = target;
        const widget = soundCloudWidgetRef.current;
        // SC.Widget assigns an object before its READY event, but transport
        // methods sent in that interval are not queued by the official wrapper.
        // Keep the target for prepareSoundCloud() instead of losing the seek.
        if (widget && soundCloudReadyRef.current) {
          beginSoundCloudSeek(widget, target);
          pendingSeekRef.current = null;
        } else {
          updateMediaPosition(target);
        }
        savePosition();
        return;
      }

      // Setting currentTime before the metadata is in throws. Hold it and let
      // `loadedmetadata` apply it.
      if (audio.readyState === 0) {
        pendingSeekRef.current = target;
        updateMediaPosition(target);
        return;
      }

      audio.currentTime = target;
      updateMediaPosition(target);
      // Throttled, not forced: a drag calls this many times a second and every
      // forced write is a synchronous trip to localStorage.
      savePosition();
    },
    [beginSoundCloudSeek, savePosition, updateMediaPosition],
  );

  const skip = useCallback(
    (seconds: number) => {
      const audio = audioRef.current;
      if (!audio || modeRef.current !== 'episode') return;
      const from = sourceRef.current === 'soundcloud'
        ? mediaPositionRef.current
        : Number.isFinite(audio.currentTime)
          ? audio.currentTime
          : (pendingSeekRef.current ?? 0);
      seek(from + seconds);
    },
    [seek],
  );

  const setVolume = useCallback((value: number) => {
    const audio = audioRef.current;
    const next = clamp(value, 0, 1);
    volumeRef.current = next;
    setVolumeState(next);
    writeStore(VOLUME_KEY, String(next));
    if (audio) audio.volume = next;
    if (sourceRef.current === 'soundcloud') {
      soundCloudWidgetRef.current?.setVolume(Math.round(next * 100));
    }
    // Dragging the slider up off zero is an unmute in every player ever built.
    if (next > 0) {
      setMuted(false);
      writeStore(MUTED_KEY, '0');
      if (audio) audio.muted = false;
      mutedRef.current = false;
    }
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((previous) => {
      const next = !previous;
      writeStore(MUTED_KEY, next ? '1' : '0');
      const audio = audioRef.current;
      if (audio) audio.muted = next;
      mutedRef.current = next;
      if (sourceRef.current === 'soundcloud') {
        soundCloudWidgetRef.current?.setVolume(next ? 0 : Math.round(volumeRef.current * 100));
      }
      return next;
    });
  }, []);

  const backToLive = useCallback(() => {
    playLive();
  }, [playLive]);

  const close = useCallback(() => {
    savePosition(true);
    clearStall();
    disposeSoundCloud();
    stopSource();
    currentRef.current = null;
    stationScheduleRequestRef.current += 1;
    stationScheduleRef.current = false;
    stationScheduleContinueRef.current = false;
    modeRef.current = 'live';
    sourceRef.current = 'native';
    pendingSeekRef.current = null;
    setCurrent(null);
    setIsStationSchedule(false);
    setStationSchedule(null);
    setMode('live');
    setSource('native');
    setEpisodeEnded(false);
    setIsPlaying(false);
    setIsLoading(false);
    updateMediaPosition(0);
    updateMediaDuration(0);
    setPlaybackError(null);
  }, [
    clearStall,
    savePosition,
    disposeSoundCloud,
    stopSource,
    updateMediaDuration,
    updateMediaPosition,
  ]);

  /* The shared SoundCloud programme behaves like the station, not like a
     manually chosen archive episode. Move a listener who was hearing it onto a
     newly opened live show, and move an active live listener back to the set
     assigned to the current window when that show closes. A paused listener
     stays paused. */
  const previousLiveRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!live) return;
    const previous = previousLiveRef.current;
    previousLiveRef.current = live.live;
    if (previous === null || previous === live.live) return;

    if (live.live && stationScheduleRef.current) {
      if (isPlaying) playLive();
      else close();
      return;
    }

    if (!live.live && previous && modeRef.current === 'live') {
      void requestStationSchedule(isPlaying);
    }
  }, [close, isPlaying, live?.live, playLive, requestStationSchedule]);

  /* The one error that clears itself: it says we have not heard from
     /api/live yet, and the moment we have, it is no longer true. */
  useEffect(() => {
    if (!live) return;
    setPlaybackError((previous) => (previous === NO_STREAM_YET ? null : previous));
  }, [live]);

  /* One bounded heartbeat credits real playback to the signed-in radio
     account. The server rejects rapid or stale intervals, and a failed credit
     never touches the audio state. */
  useEffect(() => {
    if (!account || !isPlaying) return;
    const source = mode === 'live' ? 'live' : 'archive';
    const timer = window.setInterval(() => {
      void recordListening(source, 30);
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [account?.username, isPlaying, mode, recordListening]);

  /* --------------------------------------------------- the element, wired */

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = volume;
    audio.muted = muted;
    // We are streaming, not downloading a library. Nothing is fetched until
    // somebody presses play.
    audio.preload = 'none';

    const onPlay = () => {
      if (sourceRef.current !== 'native') return;
      setIsPlaying(true);
    };
    const onPlaying = () => {
      if (sourceRef.current !== 'native') return;
      setIsPlaying(true);
      setIsLoading(false);
      setPlaybackError(null);
      clearStall();
    };
    const onPause = () => {
      if (sourceRef.current !== 'native') return;
      setIsPlaying(false);
      setIsLoading(false);
      clearStall();
      savePosition(true);
    };
    const onWaiting = () => {
      if (sourceRef.current !== 'native') return;
      setIsLoading(true);
      armStall();
    };
    const onStalled = () => {
      if (sourceRef.current !== 'native') return;
      setIsLoading(true);
      armStall();
    };
    const onCanPlay = () => {
      if (sourceRef.current !== 'native') return;
      setIsLoading(false);
      clearStall();
    };
    const onTimeUpdate = () => {
      if (modeRef.current !== 'episode') return;
      if (sourceRef.current !== 'native') return;
      updateMediaPosition(audio.currentTime);
      savePosition();
    };
    const onDurationChange = () => {
      if (sourceRef.current !== 'native') return;
      // NaN before metadata, Infinity on a live stream. Neither replaces the
      // length the API already told us.
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        updateMediaDuration(audio.duration);
      }
    };
    const onLoadedMetadata = () => {
      const wanted = pendingSeekRef.current;
      pendingSeekRef.current = null;
      if (
        wanted === null ||
        modeRef.current !== 'episode' ||
        sourceRef.current !== 'native'
      ) return;
      if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
      const target = clamp(wanted, 0, Math.max(0, audio.duration - 1));
      audio.currentTime = target;
      updateMediaPosition(target);
    };
    const onEnded = () => {
      if (sourceRef.current !== 'native') return;
      setIsPlaying(false);
      setIsLoading(false);
      setEpisodeEnded(true);
      const episode = currentRef.current;
      // Finished. Next time it starts at the top.
      if (episode) dropStore(positionKey(episode.catalogue_no));
    };
    const onError = () => {
      if (sourceRef.current !== 'native') return;
      const failure = audio.error;
      // No error object, or one we caused by dropping the source ourselves.
      if (!failure || failure.code === failure.MEDIA_ERR_ABORTED) return;

      setIsPlaying(false);
      setIsLoading(false);
      clearStall();

      if (failure.code === failure.MEDIA_ERR_DECODE) {
        setPlaybackError(DECODE_FAILED);
        return;
      }
      if (failure.code === failure.MEDIA_ERR_SRC_NOT_SUPPORTED) {
        // A 404 on the mount lands here: there is no body to decode, so the
        // browser reports the source as unusable rather than as a network fault.
        setPlaybackError(modeRef.current === 'live' ? LIVE_MOUNT_DOWN : EPISODE_GONE);
        return;
      }
      setPlaybackError(NETWORK_LOST);
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('stalled', onStalled);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    // A phone closing the tab never fires `beforeunload`. `pagehide` does.
    const onPageHide = () => savePosition(true);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('stalled', onStalled);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      window.removeEventListener('pagehide', onPageHide);
      clearStall();
    };
    // Mount only. `volume` and `muted` seed the element here and are kept in
    // step by their own setters; putting them in the deps would rebind eleven
    // listeners every time somebody drags the slider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------------------------------------------------------- media session */

  /* What the lock screen says. Reduced to three strings FIRST, so that the
     fifteen-second /api/live poll — which returns an equal-but-new object every
     time — cannot rebuild MediaMetadata on a loop. Some phones redraw the
     notification every time it is assigned, and a lock screen that flickers
     four times a minute is worse than one that says nothing. */
  const nowSource = mode === 'episode' ? current : null;
  const liveNow = live?.now ?? null;
  const shuffling = live?.fallback?.playing ?? null;

  const metaTitle = nowSource
    ? (nowSource.show?.title ?? nowSource.title)
    : liveNow
      ? (liveNow.show?.title ?? liveNow.title)
      : (shuffling?.title ?? live?.fallback?.label ?? 'Higher Ground Radio');

  const metaArtist =
    hostNames(nowSource?.hosts ?? liveNow?.hosts ?? shuffling?.hosts ?? []) ||
    'Higher Ground Radio';

  const metaAlbum = `${
    nowSource?.catalogue_no ?? liveNow?.catalogue_no ?? shuffling?.catalogue_no ?? 'HGR'
  } · Higher Ground Radio`;

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: metaTitle,
        artist: metaArtist,
        album: metaAlbum,
      });
    } catch {
      // Some engines expose mediaSession without the MediaMetadata constructor.
    }
  }, [metaAlbum, metaArtist, metaTitle]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  /* The buttons a car dashboard, a lock screen and a Bluetooth remote press. */
  const canSeek = mode === 'episode' && current !== null;

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const session = navigator.mediaSession;

    // Chromium throws NotSupportedError for actions it does not implement, so
    // every one of these is individually survivable.
    const set = (action: MediaSessionAction, handler: MediaSessionActionHandler | null): void => {
      try {
        session.setActionHandler(action, handler);
      } catch {
        // This browser does not do that action. Nothing to fix.
      }
    };

    set('play', () => {
      if (modeRef.current === 'episode' && currentRef.current) {
        if (sourceRef.current === 'soundcloud') attemptSoundCloudPlay();
        else attemptPlay();
      } else playLive();
    });
    set('pause', () => pause());
    set('stop', () => pause());

    if (canSeek) {
      set('seekbackward', (details) => skip(-(details.seekOffset ?? 15)));
      set('seekforward', (details) => skip(details.seekOffset ?? 15));
      set('seekto', (details) => {
        if (typeof details.seekTime === 'number') seek(details.seekTime);
      });
    } else {
      // Live cannot be scrubbed, so the dashboard should not offer to scrub it.
      set('seekbackward', null);
      set('seekforward', null);
      set('seekto', null);
    }
    set('previoustrack', null);
    set('nexttrack', null);
  }, [attemptPlay, attemptSoundCloudPlay, canSeek, pause, playLive, seek, skip]);

  /* The lock-screen scrubber. Episodes only, and only once we have a real
     length — setPositionState throws if position is past duration. */
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const session = navigator.mediaSession;
    if (typeof session.setPositionState !== 'function') return;

    if (mode !== 'episode' || mediaDuration <= 0) {
      try {
        session.setPositionState();
      } catch {
        // Clearing is optional; some engines refuse an empty call.
      }
      return;
    }

    try {
      session.setPositionState({
        duration: mediaDuration,
        playbackRate: 1,
        position: clamp(mediaPosition, 0, mediaDuration),
      });
    } catch {
      // A position the engine disagrees with. Not worth a broken player.
    }
  }, [mediaDuration, mediaPosition, mode]);

  /* -------------------------------------------------------------- context */

  const value = useMemo<PlayerValue>(
    () => ({
      live,
      mode,
      source,
      isPlaying,
      isLoading,
      soundCloudReady,
      episodeEnded,
      isStationSchedule,
      stationSchedule,
      // A failed poll only becomes the listener's problem when it left us with
      // nothing to show. With a last good answer on screen, the stream is very
      // probably still playing and saying otherwise would be a lie.
      error: playbackError ?? (live === null ? liveError : null),
      current,
      position: mode === 'episode' ? mediaPosition : liveElapsed,
      duration: mode === 'episode' ? mediaDuration : liveShowLength,
      volume,
      muted,
      playLive,
      playEpisode,
      toggle,
      pause,
      seek,
      skip,
      setVolume,
      toggleMute,
      backToLive,
      close,
    }),
    [
      backToLive,
      close,
      current,
      isLoading,
      isPlaying,
      live,
      liveElapsed,
      liveError,
      liveShowLength,
      mediaDuration,
      mediaPosition,
      mode,
      muted,
      pause,
      playEpisode,
      playLive,
      playbackError,
      seek,
      setVolume,
      skip,
      soundCloudReady,
      episodeEnded,
      isStationSchedule,
      stationSchedule,
      source,
      toggle,
      toggleMute,
      volume,
    ],
  );

  return (
    <PlayerContext.Provider value={value}>
      {/* The station's only audio element. It is first in the tree so that it
          never moves, it carries no src prop so React never touches the source,
          and it outlives every route in the app. An <audio> with no controls
          draws nothing — this occupies no space. */}
      <audio ref={audioRef} preload="none" />
      {soundCloudFrameUrl ? (
        <iframe
          key={soundCloudFrameGeneration}
          ref={soundCloudFrameRef}
          src={soundCloudFrameUrl}
          title="SoundCloud playback transport"
          allow="autoplay"
          aria-hidden="true"
          tabIndex={-1}
          style={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: '1px',
            height: '1px',
            border: 0,
            opacity: 0,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      {children}
    </PlayerContext.Provider>
  );
}
